import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    ShieldCheck,
    Sparkles,
    Wallet,
    Handshake,
    MapPin,
    Mail,
    HelpCircle,
    Globe,
    Plane,
    Home as HomeIcon,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import CartoonSticker from "@/components/site/CartoonStickers";

const AUDIENCES = [
    { Icon: Plane, title: "International clients", body: "Moving to Toronto from abroad — first-time arrivals or repeat moves." },
    { Icon: Globe, title: "Out-of-province clients", body: "Relocating to the GTA from another Canadian city." },
    { Icon: HomeIcon, title: "Local Toronto / GTA movers", body: "Moving within Toronto or the wider GTA." },
];

const SUPPORTED = [
    "Free consultation",
    "Relocation planning",
    "Rental shortlisting",
    "Viewing on behalf",
    "Move-day coordination",
    "Packing support coordination",
    "Cleaning support coordination",
    "Task partner coordination",
];

const VALUES = [
    {
        Icon: ShieldCheck,
        title: "Clarity First",
        body: "We confirm scope, pricing, and expectations before paid work begins.",
    },
    {
        Icon: Sparkles,
        title: "Practical Support",
        body: "We focus on the real moving tasks that make the process stressful.",
    },
    {
        Icon: Wallet,
        title: "Transparent Pricing",
        body: "We avoid silent extensions and confirm additional time before it is billed.",
    },
    {
        Icon: Handshake,
        title: "Trusted Coordination",
        body: "We support communication between clients, task partners, and service providers.",
    },
    {
        Icon: MapPin,
        title: "Local Toronto / GTA Focus",
        body: "We focus on practical relocation and move support within Toronto and the GTA.",
    },
];

export default function About() {
    return (
        <div data-testid="about-page">
            {/* HERO */}
            <section className="section relative gradient-warm overflow-hidden pb-10 md:pb-14">
                <span className="float-y absolute -top-10 right-10 h-40 w-40 rounded-full bg-[#F26B1F]/10 blur-3xl" />
                <CartoonSticker name="house" width={84} rotate={-6} className="hidden xl:inline-flex bottom-8 right-10" />
                <CartoonSticker name="boxes" width={72} rotate={8} className="hidden xl:inline-flex top-10 left-6" />

                <div className="container-atm relative">
                    <Reveal>
                        <span className="eyebrow">About</span>
                        <h1 className="mt-4 max-w-3xl text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">
                            About{" "}
                            <span className="bg-gradient-to-r from-[#F26B1F] to-[#FF8E47] bg-clip-text text-transparent">
                                AllTask Moves.
                            </span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-5 max-w-2xl text-base text-[#475569] md:text-lg">
                            Practical relocation and move-coordination support for Toronto
                            and the GTA.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* STORY */}
            <section className="section pt-6 md:pt-10">
                <div className="container-atm grid gap-10 md:grid-cols-12 md:items-start">
                    <Reveal className="md:col-span-5">
                        <span className="eyebrow">Our story</span>
                        <h2 className="mt-3 font-display text-3xl font-semibold leading-[1.1] text-[#0E2A47] sm:text-4xl">
                            Moving is rarely{" "}
                            <span className="bg-gradient-to-r from-[#F26B1F] to-[#FF8E47] bg-clip-text text-transparent">
                                one task.
                            </span>
                        </h2>
                    </Reveal>
                    <div className="md:col-span-7">
                        <Reveal delay={0.05}>
                            <p className="text-[15px] leading-relaxed text-[#475569] md:text-base">
                                AllTask Moves was built around a simple idea: moving is rarely
                                one task. It is a chain of decisions, timing, vendors,
                                communication, access, packing, cleaning, viewings, and local
                                coordination.
                            </p>
                            <p className="mt-4 text-[15px] leading-relaxed text-[#475569] md:text-base">
                                For people moving to Toronto from abroad, relocating from
                                another Canadian city, or moving within the GTA, these details
                                can become stressful when handled alone.
                            </p>
                            <p className="mt-4 text-[15px] leading-relaxed text-[#475569] md:text-base">
                                AllTask Moves helps clients coordinate the practical side of
                                their move with clear pricing, structured communication, and
                                trusted local support.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* BRAND PROMISE */}
            <section className="section">
                <div className="container-atm">
                    <Reveal>
                        <div className="card-3d mx-auto max-w-4xl p-8 md:p-12">
                            <span className="eyebrow">Brand promise</span>
                            <p className="mt-4 font-display text-2xl font-semibold leading-tight text-[#0E2A47] md:text-4xl">
                                We do not promise magic.{" "}
                                <span className="bg-gradient-to-r from-[#F26B1F] to-[#FF8E47] bg-clip-text text-transparent">
                                    We promise structure.
                                </span>
                            </p>
                            <p className="mt-5 text-[15px] leading-relaxed text-[#475569] md:text-base">
                                We cannot remove every uncertainty from a move, but we can
                                help reduce confusion, organize agreed tasks, and communicate
                                clearly throughout the process.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* WHO WE HELP */}
            <section className="section gradient-cream">
                <div className="container-atm">
                    <Reveal>
                        <span className="eyebrow">Who we help</span>
                        <h2 className="mt-3 max-w-3xl font-display text-3xl font-semibold leading-[1.1] text-[#0E2A47] sm:text-4xl">
                            Built for moves into, across, and within the GTA.
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-5 sm:grid-cols-3">
                        {AUDIENCES.map((a, i) => (
                            <motion.div
                                key={a.title}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.45, delay: i * 0.05 }}
                                className="card-soft p-6"
                                data-testid={`audience-${i}`}
                            >
                                <span className="inline-flex rounded-xl bg-[#0E2A47] p-2.5 text-white">
                                    <a.Icon size={18} strokeWidth={1.8} />
                                </span>
                                <h3 className="mt-4 font-display text-lg font-semibold text-[#0E2A47]">
                                    {a.title}
                                </h3>
                                <p className="mt-2 text-[14px] leading-relaxed text-[#475569]">
                                    {a.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHAT WE SUPPORT */}
            <section className="section">
                <div className="container-atm">
                    <Reveal>
                        <span className="eyebrow">What we support</span>
                        <h2 className="mt-3 max-w-3xl font-display text-3xl font-semibold leading-[1.1] text-[#0E2A47] sm:text-4xl">
                            Practical, real-life parts of moving.
                        </h2>
                    </Reveal>
                    <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {SUPPORTED.map((s) => (
                            <span
                                key={s}
                                className="rounded-2xl border border-[#ECE4D3] bg-white px-4 py-3 text-center text-sm font-semibold text-[#0E2A47]"
                            >
                                {s}
                            </span>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <Link
                            to="/services"
                            className="mt-8 inline-flex items-center gap-1 text-sm font-semibold text-[#F26B1F] hover:gap-2"
                            data-testid="about-explore-services"
                        >
                            Explore all services <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* CORE VALUES */}
            <section className="section gradient-cream">
                <div className="container-atm">
                    <Reveal>
                        <span className="eyebrow">Core values</span>
                        <h2 className="mt-3 max-w-3xl font-display text-3xl font-semibold leading-[1.1] text-[#0E2A47] sm:text-4xl">
                            How we work, every move.
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {VALUES.map((v, i) => (
                            <motion.div
                                key={v.title}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.45, delay: i * 0.05 }}
                                className="card-soft relative overflow-hidden p-6"
                                data-testid={`value-${i}`}
                            >
                                <span className="absolute -top-3 right-3 font-display text-4xl font-extrabold text-[#F26B1F]/10">
                                    0{i + 1}
                                </span>
                                <span className="inline-flex rounded-xl bg-[#F26B1F] p-2.5 text-white">
                                    <v.Icon size={18} strokeWidth={1.8} />
                                </span>
                                <h3 className="mt-4 font-display text-lg font-semibold text-[#0E2A47]">
                                    {v.title}
                                </h3>
                                <p className="mt-2 text-[14px] leading-relaxed text-[#475569]">
                                    {v.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICE BOUNDARY */}
            <section className="section">
                <div className="container-atm">
                    <Reveal>
                        <div className="card-3d mx-auto max-w-3xl p-8 md:p-10">
                            <div className="flex items-start gap-4">
                                <span className="rounded-2xl bg-[#0E2A47] p-3 text-white">
                                    <ShieldCheck size={22} strokeWidth={1.6} />
                                </span>
                                <div>
                                    <h3 className="text-xl font-semibold">Service boundary</h3>
                                    <p className="mt-3 text-[15px] leading-relaxed text-[#475569]">
                                        AllTask Moves provides relocation and move-coordination
                                        support. We do not provide legal, immigration, real
                                        estate brokerage, financial, property inspection,
                                        guaranteed housing, customs, or international freight
                                        services.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* CONTACT + CTA */}
            <section className="section gradient-cream pb-24">
                <div className="container-atm">
                    <div className="grid gap-6 md:grid-cols-12 md:items-center">
                        <Reveal className="md:col-span-7">
                            <span className="eyebrow">Get in touch</span>
                            <h2 className="mt-3 font-display text-3xl font-semibold leading-[1.1] text-[#0E2A47] sm:text-4xl">
                                Let's coordinate{" "}
                                <span className="bg-gradient-to-r from-[#F26B1F] to-[#FF8E47] bg-clip-text text-transparent">
                                    your next move.
                                </span>
                            </h2>
                            <div className="mt-6 grid gap-4 sm:grid-cols-2">
                                <ContactRow Icon={MapPin} label="Location" value="Toronto / GTA, Canada" />
                                <ContactRow Icon={Mail} label="Email" value="info@alltasksolutions.ca" href="mailto:info@alltasksolutions.ca" testid="about-email-link" />
                            </div>
                        </Reveal>

                        <Reveal delay={0.1} className="md:col-span-5">
                            <div className="card-soft p-6 md:p-8">
                                <p className="text-sm font-semibold uppercase tracking-widest text-[#F26B1F]">
                                    Start here
                                </p>
                                <p className="mt-3 font-display text-xl font-semibold text-[#0E2A47] md:text-2xl">
                                    Free 20-minute consultation. No payment needed.
                                </p>
                                <Link
                                    to="/book?service=free_consultation"
                                    className="btn-primary mt-6 inline-flex w-full justify-center"
                                    data-testid="about-book-cta"
                                >
                                    Book Free Consultation <ArrowRight size={16} />
                                </Link>
                                <Link
                                    to="/faq"
                                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E2A47]/70 transition-colors hover:text-[#F26B1F]"
                                    data-testid="about-faq-link"
                                >
                                    <HelpCircle size={14} /> Have questions? Visit our FAQ page.
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}

function ContactRow({ Icon, label, value, href, testid }) {
    const Inner = (
        <>
            <span className="inline-flex shrink-0 rounded-xl bg-[#0E2A47] p-2.5 text-white">
                <Icon size={16} strokeWidth={1.8} />
            </span>
            <span>
                <span className="block text-[10px] font-bold uppercase tracking-widest text-[#0E2A47]/50">
                    {label}
                </span>
                <span className="mt-1 block text-sm font-semibold text-[#0E2A47]">{value}</span>
            </span>
        </>
    );
    return href ? (
        <a href={href} data-testid={testid} className="flex items-start gap-3 rounded-2xl border border-[#ECE4D3] bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-[#F26B1F]">
            {Inner}
        </a>
    ) : (
        <div className="flex items-start gap-3 rounded-2xl border border-[#ECE4D3] bg-white p-4">
            {Inner}
        </div>
    );
}
